import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { Language, LanguageMap } from "@domain/user/locale/language";
import { LanguageHelper } from "@domain/user/locale/language-helper";
import { HighlightSearchDirective } from "@odx/angular/cdk/autocomplete-control";
import { AutocompleteModule, AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { FormFieldComponent } from "@odx/angular/components/form-field";
import { Subject, distinctUntilChanged, takeUntil } from "rxjs";

@Component({
  selector: "app-language-selection-form-field",
  imports: [
    AutocompleteSearchFilterPipe,
    FormFieldComponent,
    UiKitModule,
    AutocompleteModule,
    HighlightSearchDirective,
    ReactiveFormsModule,
  ],
  providers: [],
  templateUrl: "./language-selection-form-field.component.html",
  styleUrl: "./language-selection-form-field.component.scss",
})
export class LanguageSelectionFormFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() languages: Language[] = [];
  @Input() formGroup!: FormGroup;
  @Input() disabled: boolean = false;
  @Input() label?: string;
  @Output() selectLanguage = new EventEmitter<string>();

  protected readonly controlName = "language";
  protected localizedLanguageNames: string[] = [];
  private languageMaps: LanguageMap[] = [];

  protected noLanguageFoundMessage: string = $localize`:@@selectLanguage.search.noLanguage:Keine Sprache gefunden`;
  private languageHelper = inject(LanguageHelper);
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.createLanguageMapFromLanguageCodes();
    this.initLocalizedLanguages();

    const langControl = this.formGroup?.get(this.controlName);
    if (!langControl) return;

    langControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.syncFromControlValue(value);
    });

    this.syncFromControlValue(langControl.value);
  }

  ngOnChanges() {
    this.createLanguageMapFromLanguageCodes();
    this.initLocalizedLanguages();
    this.syncFromControlValue(this.formGroup?.get(this.controlName)?.value);
  }

  private createLanguageMapFromLanguageCodes() {
    this.languageMaps = this.languageHelper.buildLanguageMapsFromCodes(this.languages);
  }

  private initLocalizedLanguages() {
    this.localizedLanguageNames = [];

    for (const languageMap of this.languageMaps) {
      this.localizedLanguageNames.push(languageMap.localizedName);
    }

    this.localizedLanguageNames.sort();
  }

  private mapLocalizedNameToLanguageCode(localizedName: string | null | undefined) {
    for (const languageMap of this.languageMaps) {
      if (languageMap.localizedName === localizedName) return languageMap.code;
    }
    return undefined;
  }

  private syncFromControlValue(localizedName: string | null | undefined) {
    if (!localizedName) return;

    const languageCode = this.mapLocalizedNameToLanguageCode(localizedName);
    if (languageCode) this.selectLanguage.emit(languageCode);
  }

  onSelect(language: string) {
    const languageCode = this.mapLocalizedNameToLanguageCode(language);
    if (languageCode) this.selectLanguage.emit(languageCode);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
